import React, { useRef } from 'react';
import {
  Modal,
  ModalHeader,
  ModalSection,
  ModalFooter,
  Button,
  Card,
  CardSection,
  CondensedHeader,
  CondensedMedium,
  Gutter,
  ToastUtil,
  ReadonlyTextarea,
  ClipboardUtil,
} from '@axiom/ui';
import type {
  AhqSubmissionCandidateOpportunity,
  Opportunity,
  OpportunityCandidate,
} from '@axiom/validation';

import { OpportunitySubmissionUtil } from './opportunity-submission-util';

type Props = {
  candidates: AhqSubmissionCandidateOpportunity[];
  opportunity: Opportunity;
  AFCLink: string;
  onClose: () => void;
};

export const OpportunitySubmissionCopyTalentSummaries = ({
  candidates,
  opportunity,
  AFCLink,
  onClose,
}: Props) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const rankSort = (
    a: AhqSubmissionCandidateOpportunity,
    b: AhqSubmissionCandidateOpportunity
  ) => (a.submissionRank ?? Infinity) - (b.submissionRank ?? Infinity);

  const curatedCandidates = candidates
    .filter(c =>
      OpportunitySubmissionUtil.hasSubmittableDescription({
        candidate: c as unknown as OpportunityCandidate,
        opportunity,
      })
    )
    .sort(rankSort);

  const afcSubmittedCandidates = candidates
    .filter(c =>
      OpportunitySubmissionUtil.isAfcSubmitted({
        candidate: c as unknown as OpportunityCandidate,
        opportunity,
      })
    )
    .sort(rankSort);

  const activeCandidates = [...curatedCandidates, ...afcSubmittedCandidates];

  const handleCopy = async () => {
    const html = contentRef.current?.innerHTML ?? '';
    const text = contentRef.current?.textContent ?? '';
    await ClipboardUtil.copy({ html, text });
    ToastUtil.add({
      name: 'COPY_TALENT_SUMMARIES_SUCCESS',
      type: 'info',
      children: (
        <CondensedMedium>Talent summaries copied to clipboard</CondensedMedium>
      ),
    });
  };

  return (
    <Modal name="copy-talent-summaries-modal">
      <ModalHeader name="copy-talent-summaries-header" onClose={onClose}>
        Copy talent summaries
      </ModalHeader>
      <ModalSection>
        <CondensedHeader name="copy-talent-summaries-title">
          Summaries are ready to copy and email - edit first if needed
        </CondensedHeader>
        <Gutter bottom="16px" />
        <Card name="TALENT_SUMMARIES_CONTENT">
          <CardSection>
            <div ref={contentRef}>
              {activeCandidates.map(c => {
                const talentUrl = `${AFCLink}/${c.candidate.id}`;
                const [first, second] = (
                  c.candidate.calculatedDisplayName ?? ''
                )
                  .trim()
                  .split(/\s+/);
                const shortName = second
                  ? `${first} ${second[0]}.`
                  : (c.candidate.calculatedDisplayName ?? '');
                return (
                  <div key={c.candidate.id}>
                    <div data-test={`TALENT_SUMMARY_${c.candidate.id}`}>
                      <CondensedMedium>
                        <a href={talentUrl} target="_blank" rel="noreferrer">
                          {shortName}
                        </a>
                      </CondensedMedium>
                      {c.submissionDescription ? (
                        <ReadonlyTextarea
                          name={`SUBMISSION_DESCRIPTION_${c.candidate.id}`}
                          value={c.submissionDescription}
                        />
                      ) : null}
                    </div>
                    <br />
                  </div>
                );
              })}
              <CondensedMedium name="TALENT_SUMMARIES_FOOTER">
                See full list of talent here:{' '}
                <a href={AFCLink} target="_blank" rel="noreferrer">
                  {AFCLink}
                </a>
              </CondensedMedium>
            </div>
          </CardSection>
        </Card>
      </ModalSection>
      <ModalFooter>
        <Button name="cancelButton" variation="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button name="copyButton" onClick={handleCopy}>
          Copy
        </Button>
      </ModalFooter>
    </Modal>
  );
};
